import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PageContainer, Section, StatGrid } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import DataSourceService from '../../../services/DataSourceService';
import SemanticModelService from '../../../services/SemanticModelService';
import SemanticDatabaseService from '../../../services/SemanticDatabaseService';
import SemanticWorkspaceService from '../../../services/SemanticWorkspaceService';
import AgentService from '../../../services/AgentService';
import KnowledgeBaseService from '../../../services/KnowledgeBaseService';
import { listSignupAllowlist } from '../../../services/SignupAllowlistService';

// Normaliza respuestas heterogéneas (array | {items} | {agents} | null) a array.
const asList = (res, key) => {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res[key])) return res[key];
  if (res && Array.isArray(res.items)) return res.items;
  if (res && Array.isArray(res.agents)) return res.agents;
  return [];
};

const STATUS_META = {
  ok: { label: 'OK', color: 'var(--wb-success)', bg: 'var(--wb-x-bg-4ade8029, rgba(74, 222, 128, 0.16))', Icon: CheckCircleOutlineIcon },
  warn: { label: 'Atención', color: 'var(--wb-warning)', bg: 'var(--wb-x-bg-fbbf2429, rgba(251, 191, 36, 0.16))', Icon: WarningAmberIcon },
  info: { label: 'Informativo', color: 'var(--wb-accent)', bg: 'var(--wb-x-bg-38bdf829, rgba(56, 189, 248, 0.16))', Icon: InfoOutlinedIcon }
};

const StatusChip = ({ level }) => {
  const meta = STATUS_META[level] || STATUS_META.info;
  const { Icon } = meta;
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ color: `${meta.color} !important`, fontSize: 16 }} />}
      label={meta.label}
      sx={{ color: meta.color, backgroundColor: meta.bg, fontWeight: 600 }}
    />
  );
};

const DashboardCard = ({ title, subtitle, level, loading, error, stats, actions }) => (
  <Grid item xs={12} md={6}>
    <Box
      sx={{
        backgroundColor: 'var(--wb-bg-panel)',
        borderRadius: 2,
        border: '1px solid var(--wb-border)',
        p: { xs: 2, md: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ color: 'var(--wb-text)', fontWeight: 600 }}>{title}</Typography>
          {subtitle && (
            <Typography sx={{ color: 'var(--wb-muted-70)', mt: 0.5, fontSize: '0.9rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {!loading && !error && <StatusChip level={level} />}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={26} />
        </Box>
      ) : error ? (
        <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-x-bg-fbbf241a, rgba(251, 191, 36, 0.1))', color: 'var(--wb-warning)' }}>
          No se pudo cargar esta información.
        </Alert>
      ) : (
        <StatGrid items={stats} />
      )}

      <Box sx={{ flexGrow: 1 }} />
      {actions && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2.5, gap: 1 }}>
          {actions}
        </Stack>
      )}
    </Box>
  </Grid>
);

const TenantDashboard = () => {
  const { accountId, isPlatformRoot } = useContext(DataContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const tasks = {
      connections: DataSourceService.fetchDataSources(),
      models: SemanticModelService.list(),
      databases: accountId ? SemanticDatabaseService.list(accountId) : Promise.resolve([]),
      workspaces: accountId ? SemanticWorkspaceService.listByAccount(accountId) : Promise.resolve({ items: [] }),
      // listAllAgents y no listAgents: las dos métricas de esta tarjeta
      // ("Agentes activos" y "Modelos sin agente") son agregados del
      // tenant. Con una sola página la primera queda capada en silencio y
      // la segunda queda directamente mal -- sobrecuenta, porque los
      // agentes que no entraron parecen no existir.
      agents: AgentService.listAllAgents(),
      kbs: KnowledgeBaseService.listKnowledgeBases(),
      ...(isPlatformRoot ? { allowlist: listSignupAllowlist() } : {})
    };

    const keys = Object.keys(tasks);
    const settled = await Promise.allSettled(keys.map((k) => tasks[k]));
    const next = {};
    keys.forEach((k, i) => {
      next[k] = settled[i].status === 'fulfilled'
        ? { value: settled[i].value, error: false }
        : { value: null, error: true };
    });
    setData(next);
    setLoading(false);
  }, [accountId, isPlatformRoot]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = useMemo(() => {
    const conn = data.connections || {};
    const connList = asList(conn.value);
    const failedConn = connList.filter(
      (c) => ['error', 'failed'].includes(String(c?.status || '').toLowerCase()) || c?.errorMessageUser
    ).length;
    const modelsList = asList(data.models?.value, 'items');
    const dbList = asList(data.databases?.value, 'items');
    const wsList = asList(data.workspaces?.value, 'items');
    const agentsList = asList(data.agents?.value, 'agents');
    const activeAgents = agentsList.filter((a) => String(a?.agentStatus || '').toUpperCase() === 'ACTIVE');
    const modelsWithAgent = new Set(activeAgents.map((a) => a.semanticModelId).filter(Boolean)).size;
    const modelsWithoutAgent = Math.max(0, modelsList.length - modelsWithAgent);
    const kbList = asList(data.kbs?.value);
    const kbFailed = kbList.filter((k) => String(k?.status || '').toUpperCase() === 'FAILED').length;

    const list = [];

    list.push({
      key: 'semantic',
      title: 'Conexiones y modelo semántico',
      subtitle: 'Fuentes de datos, modelos, bases de datos y espacios de análisis.',
      loading,
      error: Boolean(conn.error && data.models?.error),
      level: failedConn > 0 ? 'warn' : (connList.length === 0 || modelsList.length === 0) ? 'info' : 'ok',
      stats: [
        { label: 'Conexiones', value: conn.error ? '—' : connList.length },
        { label: 'Con error', value: conn.error ? '—' : failedConn },
        { label: 'Modelos semánticos', value: data.models?.error ? '—' : modelsList.length },
        { label: 'Bases de datos', value: data.databases?.error ? '—' : dbList.length },
        { label: 'Workspaces', value: data.workspaces?.error ? '—' : wsList.length }
      ],
      actions: [
        <Button key="c" size="small" variant="outlined" onClick={() => navigate('/admin/home/semantic-models/conexiones')}>
          Conexiones
        </Button>,
        <Button key="m" size="small" variant="outlined" onClick={() => navigate('/admin/home/semantic-models/modelos')}>
          Modelos
        </Button>,
        <Button key="w" size="small" variant="outlined" onClick={() => navigate('/admin/home/workspaces/espacios-analisis')}>
          Workspaces
        </Button>
      ]
    });

    list.push({
      key: 'agents',
      title: 'Agentes y bases de conocimiento',
      subtitle: 'Un agente ACTIVE por modelo; su base de conocimiento asociada.',
      loading,
      error: Boolean(data.agents?.error && data.kbs?.error),
      level: kbFailed > 0 ? 'warn' : activeAgents.length === 0 ? 'info' : 'ok',
      stats: [
        { label: 'Agentes activos', value: data.agents?.error ? '—' : activeAgents.length },
        { label: 'Modelos sin agente', value: (data.agents?.error || data.models?.error) ? '—' : modelsWithoutAgent },
        { label: 'Bases de conocimiento', value: data.kbs?.error ? '—' : kbList.length },
        { label: 'KB con error', value: data.kbs?.error ? '—' : kbFailed }
      ],
      actions: [
        <Button key="a" size="small" variant="outlined" onClick={() => navigate('/admin/home/agentes/listado')}>
          Agentes
        </Button>,
        <Button key="k" size="small" variant="outlined" onClick={() => navigate('/admin/home/agentes/bases-conocimiento')}>
          Bases de conocimiento
        </Button>
      ]
    });

    if (isPlatformRoot) {
      const allowList = asList(data.allowlist?.value, 'items');
      list.push({
        key: 'allowlist',
        title: 'Correos de registro permitidos',
        subtitle: 'Whitelist de correos que pueden registrarse aunque su dominio sea público.',
        loading,
        error: Boolean(data.allowlist?.error),
        level: 'info',
        stats: [
          { label: 'Correos en la lista', value: data.allowlist?.error ? '—' : allowList.length }
        ],
        actions: [
          <Button key="s" size="small" variant="outlined" onClick={() => navigate('/admin/home/seguridad/registro-permitido')}>
            Gestionar
          </Button>
        ]
      });
    }

    return list;
  }, [data, loading, isPlatformRoot, navigate]);

  const attention = cards.filter((c) => !c.error && c.level === 'warn').length;

  return (
    <PageContainer
      title="Panel del tenant"
      description="Estado de las configuraciones de administración de tu organización."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Actualizar
        </Button>
      ]}
    >
      <Section
        title="Resumen"
        subtitle={
          loading
            ? 'Cargando estado...'
            : attention > 0
              ? `${attention} área(s) requieren atención.`
              : 'Todo en orden.'
        }
      >
        <Grid container spacing={2}>
          {cards.map((card) => (
            <DashboardCard key={card.key} {...card} />
          ))}
        </Grid>
      </Section>
    </PageContainer>
  );
};

export default TenantDashboard;
